import { Loader } from '@googlemaps/js-api-loader'
import { useState, useEffect } from 'react'

// client-side singletons
/* global google */
/* eslint no-undef: "error" */
let _placesAutocompleteServiceInstance
let _placesServiceInstance
let _geocoderInstance

const _getBrowserServices = async () => {
  if (!_placesAutocompleteServiceInstance || !_placesServiceInstance || !_geocoderInstance) {
    const apiLoader = new Loader({
      apiKey: process.env.GOOGLE_APIS_CLIENT_KEY,
      sessionToken: true,
      libraries: ['places', 'geometry'],
      version: 'weekly',
    })
    const google = await apiLoader.load()
    _placesAutocompleteServiceInstance = new google.maps.places.AutocompleteService()
    _placesServiceInstance = new google.maps.places.PlacesService(document.createElement('div'))
    _geocoderInstance = new google.maps.Geocoder()
  }
  return {
    placesAutocompleteService: _placesAutocompleteServiceInstance,
    placesService: _placesServiceInstance,
    geocoder: _geocoderInstance,
    google: google || null,
  }
}

const _queryZipCodes = async (query) => {
  const { placesAutocompleteService } = await _getBrowserServices()
  const result = await placesAutocompleteService.getPlacePredictions({
    input: query,
    types: ['(regions)'],
    componentRestrictions: { country: 'us' },
  })
  return result.predictions.map((p) => {
    return {
      description: p.description,
      placeId: p.place_id,
    }
  })
}

const _getPlaceCoordinates = async (placeId) => {
  const { placesService } = await _getBrowserServices()
  /* eslint-disable-next-line no-unused-vars */
  return new Promise((resolve, reject) => {
    placesService.getDetails({ placeId, fields: ['geometry.location'] }, (place, status) => {
      if (status === 'OK') {
        const coordinates = place.geometry.location.toJSON()
        resolve({ lat: coordinates.lat, lng: coordinates.lng })
      } else {
        resolve(null)
      }
    })
  })
}

const getZipCoordinates = async (zipCode) => {
  if (!zipCode || zipCode.length === 0) { return {} }

  const places = await _queryZipCodes(zipCode)
  if (places.length > 0) {
    const coordinates = await _getPlaceCoordinates(places[0].placeId)
    return { lat: coordinates.lat, lng: coordinates.lng }
  }
  return {}
}

const _radians = (degrees) => {
  return degrees * Math.PI / 180
}

const getDistance = (coordinates1, coordinates2) => {
  const R = 6371 // approximate radius of the earth in km

  // Convert coordinates to radians
  const radLat1 = _radians(coordinates1.lat)
  const radLon1 = _radians(coordinates1.lng)
  const radLat2 = _radians(coordinates2.lat)
  const radLon2 = _radians(coordinates2.lng)

  // Calculate the distance between the two coordinates using the Haversine formula
  const dlon = radLon2 - radLon1
  const dlat = radLat2 - radLat1
  const a = (Math.sin(dlat / 2) ** 2) + (Math.cos(radLat1) * Math.cos(radLat2) * (Math.sin(dlon / 2) ** 2))
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  const distance = R * c

  // return distance in kilometers
  return distance
}

const useLoadScripts = () => {
  const [isLoaded, setIsLoaded] = useState(false)
  const [loadError, setLoadError] = useState(null)

  useEffect(() => {
    const loadScripts = async () => {
      const { placesAutocompleteService, placesService, geocoder } = await _getBrowserServices()
      if (placesAutocompleteService && placesService && geocoder) {
        setIsLoaded(true)
      } else {
        setLoadError(true)
      }
    }
    loadScripts()
  }, [])
  return { isLoaded, loadError }
}

export {
  getZipCoordinates,
  getDistance,
  useLoadScripts,
}
